import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { useSelector } from "react-redux";

import MyDatePicker from "../../components/CardSection/MyDatePicker";
import { db } from "../../config/firebaseConfig";
import { selectActiveUid } from "../Auth/authSlice";
import { getKinshasaDateIso, isoToFrDmy } from "../../utils/dateUtils";

const REPORT_SECTIONS = [
  { key: "INDEX", label: "INDEX", types: ["INDEX"] },
  { key: "CUVES", label: "CUVES", types: ["CUVES"] },
  { key: "VERSEMENT", label: "VERSEMENT", types: ["VERS"] },
  {
    key: "APPROVISIONNEMENT",
    label: "APPROVISIONNEMENT",
    types: ["APPROV"],
  },
  {
    key: "RETRAIT_PRODUIT",
    label: "RETRAIT PRODUIT",
    types: ["RETRAIT"],
  },
  {
    key: "RETRAIT_REMB_ARGENT",
    label: "RETRAIT ET REMB. ARGENT",
    types: ["RETRAIT_ARGENT", "REMB_ARGENT"],
  },
  { key: "FERMETURE", label: "FERMETURE", types: ["FERMETURE"] },
];

const TYPE_TO_SECTION_KEY = REPORT_SECTIONS.reduce((mapping, section) => {
  for (const type of section.types) {
    mapping[type] = section.key;
  }
  return mapping;
}, {});

const PUMP_ORDER = [
  "S1",
  "S2",
  "S3",
  "S4",
  "S5",
  "S6",
  "G1",
  "G2",
  "G3",
  "G4",
  "G5",
  "G6",
];

function parseEntrySortValue(key, entry) {
  const canonical = String(key || "").match(/_[DA]$/i);
  if (canonical) {
    return String(key).endsWith("_D") ? 8 * 3600 : 20 * 3600;
  }

  const keyMatch = String(key || "").match(
    /_(\d{2})_(\d{2})_(\d{2})(?:_\d+)?$/,
  );
  if (keyMatch) {
    return (
      Number(keyMatch[1]) * 3600 +
      Number(keyMatch[2]) * 60 +
      Number(keyMatch[3])
    );
  }

  const dateMatch = String(entry?.date || "").match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!dateMatch) return 0;

  return (
    Number(dateMatch[1]) * 3600 +
    Number(dateMatch[2]) * 60 +
    Number(dateMatch[3])
  );
}

function formatFieldValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function parseIndexNumber(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const normalized = raw.replace(/[ \u202F\u00A0]/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatIndexNumber(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "-";
  }

  return value
    .toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\u202F|\u00A0/g, " ");
}

function parseIndexMoment(key, entry) {
  const canonicalMatch = String(key || "").match(/[SG]\d+_([DA])$/i);
  if (canonicalMatch) return canonicalMatch[1].toUpperCase();

  const keyMatch = String(key || "").match(/[SG]\d+_(\d{2})_(\d{2})_(\d{2})$/i);
  if (keyMatch) return Number(keyMatch[1]) < 12 ? "D" : "A";

  const dateMatch = String(entry?.date || "").match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!dateMatch) return null;
  return Number(dateMatch[1]) < 12 ? "D" : "A";
}

function buildIndexReportRows(indexEntries) {
  const byPump = Object.fromEntries(
    PUMP_ORDER.map((pump) => [pump, { depart: null, arrive: null }]),
  );

  for (const entry of indexEntries) {
    const pump = String(entry?.value?.item || "").toUpperCase();
    if (!PUMP_ORDER.includes(pump)) continue;

    const moment = parseIndexMoment(entry.key, entry.value);
    const parsedValue = parseIndexNumber(entry?.value?.value);
    if (!moment || parsedValue === null) continue;

    if (moment === "D") byPump[pump].depart = parsedValue;
    if (moment === "A") byPump[pump].arrive = parsedValue;
  }

  return PUMP_ORDER.map((pump) => {
    const depart = byPump[pump].depart;
    const arrive = byPump[pump].arrive;
    const liters =
      depart !== null && arrive !== null ? Math.max(0, arrive - depart) : null;

    return { pump, depart, arrive, liters };
  }).filter((row) => row.depart !== null || row.arrive !== null);
}

function buildGroupedEntries(dataDoc) {
  const grouped = Object.fromEntries(
    REPORT_SECTIONS.map((section) => [section.key, []]),
  );

  for (const [key, value] of Object.entries(dataDoc || {})) {
    let type = value?.type || "AUTRES";
    // Key-based fallback for entries missing a type field (e.g. legacy APPROV entries)
    if (type === "AUTRES" && key.startsWith("APPROV")) type = "APPROV";
    const sectionKey = TYPE_TO_SECTION_KEY[type];
    if (!sectionKey) continue;

    grouped[sectionKey].push({
      key,
      value,
      sort: parseEntrySortValue(key, value),
    });
  }

  for (const entries of Object.values(grouped)) {
    entries.sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key));
  }

  return grouped;
}

function availableSections(groupedEntries, rapportDoc) {
  return REPORT_SECTIONS.filter((section) => {
    if (section.key === "FERMETURE") {
      return (
        groupedEntries[section.key]?.length > 0 ||
        Object.keys(rapportDoc || {}).length > 0
      );
    }

    return groupedEntries[section.key]?.length > 0;
  });
}

function LogsEntry({ entryKey, value }) {
  const fields = Object.entries(value || {});

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {entryKey}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {fields.map(([field, fieldValue]) => (
          <Typography
            key={`${entryKey}-${field}`}
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            <strong>{field}</strong>: {formatFieldValue(fieldValue)}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}

function IndexPumpCard({ row }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {row.pump}
      </Typography>

      <Stack spacing={0.4} sx={{ mt: 0.75 }}>
        <Typography variant="body2">
          <strong>Depart</strong>: {formatIndexNumber(row.depart)}
        </Typography>
        <Typography variant="body2">
          <strong>Arrive</strong>: {formatIndexNumber(row.arrive)}
        </Typography>
        <Typography variant="body2">
          <strong>Litres</strong>: {formatIndexNumber(row.liters)}
        </Typography>
      </Stack>
    </Paper>
  );
}

function IndexReportSection({ entries }) {
  const rows = useMemo(() => buildIndexReportRows(entries), [entries]);
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        if (row.liters === null) return acc;

        if (row.pump.startsWith("S")) acc.ST += row.liters;
        if (row.pump.startsWith("G")) acc.GT += row.liters;

        return acc;
      },
      { ST: 0, GT: 0 },
    );
  }, [rows]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucune donnee index exploitable pour cette date.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Stack spacing={1}>
        {rows.map((row) => (
          <IndexPumpCard key={row.pump} row={row} />
        ))}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary">
            ST (total S)
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatIndexNumber(totals.ST)}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.25, flex: 1, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary">
            GT (total G)
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatIndexNumber(totals.GT)}
          </Typography>
        </Paper>
      </Stack>
    </Stack>
  );
}

function CuvesSection({ entries }) {
  const rows = useMemo(() => {
    return entries
      .map((entry) => {
        const cuve = String(entry?.value?.item || "").toUpperCase();
        return {
          cuve,
          value: entry?.value?.value,
        };
      })
      .filter((row) => /^C\d+$/.test(row.cuve))
      .sort((a, b) => a.cuve.localeCompare(b.cuve));
  }, [entries]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucune donnee cuve pour cette date.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.5}>
        {rows.map((row) => (
          <Typography key={row.cuve} variant="body2">
            <strong>{row.cuve}</strong>: {formatFieldValue(row.value)}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}

function VersementSection({ entries }) {
  const rows = useMemo(() => {
    return entries
      .map((entry) => ({
        item: String(entry?.value?.item || "").toUpperCase(),
        value: entry?.value?.value,
        date: entry?.value?.date || null,
        sort: entry?.sort || 0,
      }))
      .filter((row) => row.item.includes("_CDF") || row.item.includes("_USD"))
      .sort((a, b) => a.sort - b.sort || a.item.localeCompare(b.item));
  }, [entries]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucun versement pour cette date.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.5}>
        {rows.map((row, idx) => (
          <Box key={`${row.item}-${idx}`}>
            <Typography variant="body2">
              <strong>{row.item}</strong>: {formatFieldValue(row.value)}
            </Typography>
            {row.date && (
              <Typography variant="caption" color="text.secondary">
                {row.date}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function extractRetraitProduitLabel(itemValue) {
  const item = String(itemValue || "").toUpperCase();
  if (item.endsWith("_GO")) return "GO";
  if (item.endsWith("_SU")) return "SU";
  return item || "-";
}

function ApprovSection({ entries }) {
  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucun approvisionnement pour cette date.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {entries.map((entry) => {
        const v = entry.value || {};
        const cuveLines = [];
        if (v.cuve1) cuveLines.push({ cuve: v.cuve1, avant: v.avant1, apres: v.apres1 });
        if (v.cuve2) cuveLines.push({ cuve: v.cuve2, avant: v.avant2, apres: v.apres2 });

        return (
          <Paper key={entry.key} variant="outlined" sx={{ p: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
              {entry.key}
            </Typography>
            <Stack spacing={0.4}>
              {v.term && (
                <Typography variant="body2">
                  <strong>Terminal</strong> : {v.term}
                </Typography>
              )}
              {v.produit && (
                <Typography variant="body2">
                  <strong>Produit</strong> : {v.produit}
                </Typography>
              )}
              {v.qte != null && (
                <Typography variant="body2">
                  <strong>Quantité (m³)</strong> : {formatFieldValue(v.qte)}
                </Typography>
              )}
              {v.numbl && (
                <Typography variant="body2">
                  <strong>BL 1</strong> : {v.numbl}
                </Typography>
              )}
              {v.numbl2 && (
                <Typography variant="body2">
                  <strong>BL 2</strong> : {v.numbl2}
                </Typography>
              )}
              {cuveLines.map((c, i) => (
                <Typography key={i} variant="body2">
                  <strong>{c.cuve}</strong> : {formatFieldValue(c.avant)} → {formatFieldValue(c.apres)}
                </Typography>
              ))}
              {v.deb && (
                <Typography variant="body2">
                  <strong>Débordement (L)</strong> : {v.deb}
                </Typography>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function RetraitProduitSection({ entries }) {
  const rows = useMemo(() => {
    return entries
      .map((entry) => ({
        produit: extractRetraitProduitLabel(entry?.value?.item),
        value: entry?.value?.value,
        comment: entry?.value?.comment,
        sort: entry?.sort || 0,
      }))
      .sort((a, b) => a.sort - b.sort || a.produit.localeCompare(b.produit));
  }, [entries]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucun retrait produit pour cette date.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.5}>
        {rows.map((row, idx) => {
          const comment = String(row.comment || "").trim();
          return (
            <Typography key={`${row.produit}-${idx}`} variant="body2">
              <strong>{row.produit}</strong>: {formatFieldValue(row.value)}
              {comment ? ` (${comment})` : ""}
            </Typography>
          );
        })}
      </Stack>
    </Paper>
  );
}

function extractArgentGroup(entry) {
  const key = String(entry?.key || "").toUpperCase();
  if (key.startsWith("RETRAIT_ARGENT_")) return "RETRAIT_ARGENT";
  if (key.startsWith("REMB_ARGENT_")) return "REMB_ARGENT";

  const item = String(entry?.value?.item || "").toUpperCase();
  if (item.startsWith("RETRAIT_")) return "RETRAIT_ARGENT";
  if (item.startsWith("REMB_")) return "REMB_ARGENT";
  return "AUTRES";
}

function extractCurrencyLabel(itemValue) {
  const item = String(itemValue || "").toUpperCase();
  if (item.includes("_CDF")) return "CDF";
  if (item.includes("_USD")) return "USD";
  return item || "-";
}

function RetraitRembArgentSection({ entries }) {
  const grouped = useMemo(() => {
    const base = {
      RETRAIT_ARGENT: [],
      REMB_ARGENT: [],
      AUTRES: [],
    };

    for (const entry of entries) {
      const group = extractArgentGroup(entry);
      base[group].push({
        currency: extractCurrencyLabel(entry?.value?.item),
        value: entry?.value?.value,
        comment: entry?.value?.comment,
        sort: entry?.sort || 0,
      });
    }

    for (const rows of Object.values(base)) {
      rows.sort(
        (a, b) => a.sort - b.sort || a.currency.localeCompare(b.currency),
      );
    }

    return base;
  }, [entries]);

  const hasRows = Object.values(grouped).some((rows) => rows.length > 0);
  if (!hasRows) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucun retrait/remboursement argent pour cette date.
      </Typography>
    );
  }

  const sectionOrder = ["RETRAIT_ARGENT", "REMB_ARGENT", "AUTRES"];

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={1.25}>
        {sectionOrder.map((sectionName) => {
          const rows = grouped[sectionName];
          if (!rows.length) return null;

          return (
            <Box key={sectionName}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {sectionName}
              </Typography>
              <Stack spacing={0.4}>
                {rows.map((row, idx) => {
                  const comment = String(row.comment || "").trim();
                  return (
                    <Typography
                      key={`${sectionName}-${row.currency}-${idx}`}
                      variant="body2"
                    >
                      <strong>{row.currency}</strong>:{" "}
                      {formatFieldValue(row.value)}
                      {comment ? ` (${comment})` : ""}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function parseMaybeNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.replace(/[ \u202F\u00A0]/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDiff(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "-";
  }

  const sign = value > 0 ? "+" : "";
  return `${sign}${formatIndexNumber(value)}`;
}

function diffColor(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "text.secondary";
  }

  const abs = Math.abs(value);
  if (abs === 0) return "success.main";
  if (abs < 100) return "warning.main";
  return "error.main";
}

function pickLatestFermeture(entries) {
  if (!entries.length) return null;
  const sorted = [...entries].sort((a, b) => a.sort - b.sort);
  return sorted[sorted.length - 1];
}

function FermetureSection({ entries, rapportDoc }) {
  const latest = useMemo(() => pickLatestFermeture(entries), [entries]);

  if (!latest && Object.keys(rapportDoc || {}).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucune fermeture pour cette date.
      </Typography>
    );
  }

  const declaredSu = parseMaybeNumber(latest?.value?.sortie_su);
  const declaredGo = parseMaybeNumber(latest?.value?.sortie_go);
  const declaredCdfOil = parseMaybeNumber(latest?.value?.oil_cdf);
  const declaredUsdOil = parseMaybeNumber(latest?.value?.oil_usd);

  const autoSu = parseMaybeNumber(rapportDoc?.ST);
  const autoGo = parseMaybeNumber(rapportDoc?.GT);
  const autoCdfOil = parseMaybeNumber(rapportDoc?.CDF_OIL);
  const autoUsdOil = parseMaybeNumber(rapportDoc?.USD_OIL);

  const ecartSu =
    declaredSu !== null && autoSu !== null ? declaredSu - autoSu : null;
  const ecartGo =
    declaredGo !== null && autoGo !== null ? declaredGo - autoGo : null;
  const ecartCdfOil =
    declaredCdfOil !== null && autoCdfOil !== null
      ? declaredCdfOil - autoCdfOil
      : null;
  const ecartUsdOil =
    declaredUsdOil !== null && autoUsdOil !== null
      ? declaredUsdOil - autoUsdOil
      : null;

  const lines = [
    {
      label: "SU",
      declared: declaredSu,
      automatic: autoSu,
      diff: ecartSu,
    },
    {
      label: "GO",
      declared: declaredGo,
      automatic: autoGo,
      diff: ecartGo,
    },
    {
      label: "CDF_OIL",
      declared: declaredCdfOil,
      automatic: autoCdfOil,
      diff: ecartCdfOil,
    },
    {
      label: "USD_OIL",
      declared: declaredUsdOil,
      automatic: autoUsdOil,
      diff: ecartUsdOil,
    },
  ].filter((line) => line.declared !== null || line.automatic !== null);

  return (
    <Stack spacing={1.25}>
      <Paper variant="outlined" sx={{ p: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Resume fermeture
        </Typography>
        <Stack spacing={0.4} sx={{ mt: 0.75 }}>
          <Typography variant="body2">
            <strong>Pompiste</strong>: {formatFieldValue(latest?.value?.name)}
          </Typography>
          <Typography variant="body2">
            <strong>Heure</strong>: {formatFieldValue(latest?.value?.date)}
          </Typography>
        </Stack>
      </Paper>

      {lines.map((line) => (
        <Paper key={line.label} variant="outlined" sx={{ p: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {line.label}
          </Typography>
          <Stack spacing={0.35} sx={{ mt: 0.6 }}>
            <Typography variant="body2">
              <strong>Declare</strong>: {formatIndexNumber(line.declared)}
            </Typography>
            <Typography variant="body2">
              <strong>Autom.</strong>: {formatIndexNumber(line.automatic)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: diffColor(line.diff), fontWeight: 700 }}
            >
              <strong>Ecart</strong>: {formatDiff(line.diff)}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

export default function Logs() {
  const activeUid = useSelector(selectActiveUid);
  const [selectedDate, setSelectedDate] = useState(() => getKinshasaDateIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dataDoc, setDataDoc] = useState({});
  const [rapportDoc, setRapportDoc] = useState({});

  useEffect(() => {
    if (!activeUid || !selectedDate) {
      setDataDoc({});
      setRapportDoc({});
      return;
    }

    let cancelled = false;

    async function loadLogs() {
      setLoading(true);
      setError("");
      try {
        const docId = isoToFrDmy(selectedDate);
        const [dataSnap, rapportSnap] = await Promise.all([
          getDoc(doc(db, "users", activeUid, "data", docId)),
          getDoc(doc(db, "users", activeUid, "rapport", docId)),
        ]);

        if (cancelled) return;

        setDataDoc(dataSnap.exists() ? dataSnap.data() : {});
        setRapportDoc(rapportSnap.exists() ? rapportSnap.data() : {});
      } catch (loadError) {
        console.error("Error loading logs:", loadError);
        if (!cancelled) {
          setDataDoc({});
          setRapportDoc({});
          setError("Chargement impossible. Reessayez.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLogs();

    return () => {
      cancelled = true;
    };
  }, [activeUid, selectedDate]);

  const groupedEntries = useMemo(() => buildGroupedEntries(dataDoc), [dataDoc]);
  const sections = useMemo(
    () => availableSections(groupedEntries, rapportDoc),
    [groupedEntries, rapportDoc],
  );
  const hasContent = sections.length > 0;

  return (
    <Box
      sx={{
        m: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 800 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Rapport
          </Typography>
          <MyDatePicker
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </Paper>

        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !hasContent ? (
            <Alert severity="info">Aucun rapport trouve pour cette date.</Alert>
          ) : (
            <Stack spacing={2}>
              {sections.map((section) => (
                <Paper key={section.key} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {section.label}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1.25}>
                    {section.key === "INDEX" && (
                      <IndexReportSection
                        entries={groupedEntries[section.key]}
                      />
                    )}

                    {section.key === "CUVES" && (
                      <CuvesSection entries={groupedEntries[section.key]} />
                    )}

                    {section.key === "VERSEMENT" && (
                      <VersementSection entries={groupedEntries[section.key]} />
                    )}

                    {section.key === "APPROVISIONNEMENT" && (
                      <ApprovSection entries={groupedEntries[section.key]} />
                    )}

                    {section.key === "RETRAIT_PRODUIT" && (
                      <RetraitProduitSection
                        entries={groupedEntries[section.key]}
                      />
                    )}

                    {section.key === "RETRAIT_REMB_ARGENT" && (
                      <RetraitRembArgentSection
                        entries={groupedEntries[section.key]}
                      />
                    )}

                    {section.key === "FERMETURE" && (
                      <FermetureSection
                        entries={groupedEntries[section.key]}
                        rapportDoc={rapportDoc}
                      />
                    )}

                    {section.key !== "INDEX" &&
                      section.key !== "CUVES" &&
                      section.key !== "VERSEMENT" &&
                      section.key !== "APPROVISIONNEMENT" &&
                      section.key !== "RETRAIT_PRODUIT" &&
                      section.key !== "RETRAIT_REMB_ARGENT" &&
                      section.key !== "FERMETURE" &&
                      groupedEntries[section.key].map((entry) => (
                        <LogsEntry
                          key={entry.key}
                          entryKey={entry.key}
                          value={entry.value}
                        />
                      ))}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
